import {
  getTimeDifference,
  IconArrowRight,
  TimeUnit,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { getClassUrlObject } from '../../utils/classes';
import {
  CoachingSessionUrgencyEnum,
  useCoachingSessionTimeRemaining,
} from '../../utils/coaching-session';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import {
  isSessionClassOrFreeEvent,
  isSessionOneOnOne,
} from '../../utils/session';
import { getUrlObject } from '../../utils/url';

import type { CoachingSessionAlertBannerCoachingSessionFragment } from './__generated-gql-types__/CoachingSessionAlertBanner.generated';

interface CoachingSessionAlertBannerProps {
  session: CoachingSessionAlertBannerCoachingSessionFragment;
  onSessionEnd: () => void;
}

const CoachingSessionAlertBanner: React.FC<CoachingSessionAlertBannerProps> = ({
  session,
  onSessionEnd,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  const sessionTitle = isSessionOneOnOne(session.content)
    ? session.content.title
    : isSessionClassOrFreeEvent(session.content)
      ? session.content.name
      : session.content.bootcamp.title;
  const sessionId = session.id;

  const { urgency, remainingTime, isPast } = useCoachingSessionTimeRemaining(
    session.startAt,
    session.endAt,
  );

  const sessionHrefObject = isSessionOneOnOne(session.content)
    ? getUrlObject(`/meet/${sessionId}`, null)
    : isSessionClassOrFreeEvent(session.content)
      ? getClassUrlObject(session.content.slug ?? '', sessionId)
      : ''; // TODO: Update for bootcamp sessions

  const title = isSessionOneOnOne(session.content)
    ? `Your session with ${session.coach.user.firstName} is ${
        urgency === CoachingSessionUrgencyEnum.NOW
          ? `happening now!`
          : `starting in ${remainingTime}!`
      }`
    : isSessionClassOrFreeEvent(session.content)
      ? `Your ${session.content.isFreeEvent ? 'event' : 'class'} ${sessionTitle} is ${
          urgency === CoachingSessionUrgencyEnum.NOW
            ? `happening now!`
            : `starting in ${remainingTime}!`
        }`
      : `${session.content.bootcamp.title} is ${
          urgency === CoachingSessionUrgencyEnum.NOW
            ? `happening now!`
            : `starting in ${remainingTime}!`
        }`;

  const displayOneOnOneSession =
    isSessionOneOnOne(session.content) &&
    (urgency === CoachingSessionUrgencyEnum.NOW ||
      urgency === CoachingSessionUrgencyEnum.UPCOMING);

  const sessionType = isSessionOneOnOne(session.content)
    ? 'session'
    : isSessionClassOrFreeEvent(session.content)
      ? session.content.isFreeEvent
        ? 'event'
        : 'class'
      : 'bootcamp';

  useEffect(() => {
    if (isPast) {
      onSessionEnd();
    }
    // re-check when `isPast` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPast]);

  return displayOneOnOneSession || !isSessionOneOnOne(session.content) ? (
    <div
      className={
        urgency === CoachingSessionUrgencyEnum.NOW
          ? 'bg-leland-red/20'
          : urgency === CoachingSessionUrgencyEnum.UPCOMING
            ? 'bg-leland-blue-light'
            : ''
      }
    >
      <div className="flex items-center justify-between space-x-4 px-4 py-3 lg:px-6">
        <p
          className={`text-left text-base font-medium ${
            urgency === CoachingSessionUrgencyEnum.NOW
              ? 'text-leland-red'
              : urgency === CoachingSessionUrgencyEnum.UPCOMING
                ? 'text-leland-blue'
                : ''
          }`}
        >
          {title}
        </p>
        <Link
          href={sessionHrefObject}
          type="button"
          className={`flex items-center justify-center whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium text-white ${
            urgency === CoachingSessionUrgencyEnum.NOW
              ? 'bg-leland-red'
              : urgency === CoachingSessionUrgencyEnum.UPCOMING
                ? 'bg-leland-blue'
                : ''
          }`}
        >
          {isMd ? (
            urgency === CoachingSessionUrgencyEnum.NOW ||
            urgency === CoachingSessionUrgencyEnum.UPCOMING ? (
              `Join ${sessionType}`
            ) : (
              `View ${sessionType}`
            )
          ) : (
            <IconArrowRight className="size-3.5" />
          )}
        </Link>
      </div>
    </div>
  ) : null;
};

const CoachingSessionAlertBannerWrapper: React.FC = () => {
  const { currentUser } = useAuth();
  const [recentlyEndedSessionId, setRecentlyEndedSessionId] =
    useState<string>();

  const nextSession = useMemo(
    () =>
      currentUser?.applicant?.upcomingEnrolledSessions?.find(
        (session) =>
          (!recentlyEndedSessionId || session.id !== recentlyEndedSessionId) &&
          getTimeDifference(Date.now(), session.startAt, TimeUnit.MINUTE) <= 15,
      ),
    [currentUser?.applicant?.upcomingEnrolledSessions, recentlyEndedSessionId],
  );
  if (!nextSession) {
    return null;
  }

  return (
    <CoachingSessionAlertBanner
      session={nextSession}
      onSessionEnd={() => setRecentlyEndedSessionId(nextSession.id)}
    />
  );
};

export default CoachingSessionAlertBannerWrapper;
